/**
 * Type converters for recommendation products
 *
 * Provides utilities for converting between ProductResult (search-core) and
 * RecommendedProduct (UI) formats. This allows UI components to work with
 * either format while maintaining architectural boundaries.
 */

import { ProductResult } from '../types/ProductResult';
import { RecommendedProduct } from '../../types/search';

/**
 * Type guard to check if a product is a ProductResult (search-core format)
 */
export function isProductResult(product: unknown): product is ProductResult {
  if (!product || typeof product !== 'object') return false;

  const p = product as Record<string, unknown>;
  return p.type === 'product' &&
    typeof p.id !== 'undefined' &&
    typeof p.title === 'string' &&
    p.raw !== undefined;
}

/**
 * Type guard to check if a product is a RecommendedProduct (UI format)
 */
export function isRecommendedProduct(product: unknown): product is RecommendedProduct {
  if (!product || typeof product !== 'object') return false;

  const p = product as Record<string, unknown>;
  return typeof p.name === 'string' &&
    typeof p.imageUrl === 'string' &&
    typeof p.id !== 'undefined';
}

/**
 * Convert ProductResult to RecommendedProduct for UI compatibility
 *
 * Extracts category from raw data if available and maps fields appropriately.
 */
export function convertToRecommendedProduct(product: ProductResult): RecommendedProduct {
  // Extract category from raw data if available
  const rawCategory = extractCategoryFromRaw(product.raw);

  return {
    id: product.id,
    name: product.title,
    price: product.price || '',
    imageUrl: product.image || '/images/no-image.svg',
    category: rawCategory,
    actionLabel: 'В корзину',
  };
}

/**
 * Extract category from raw product data
 *
 * Searches for category information in the raw hit structure.
 * Supports both Meilisearch and other backend formats.
 */
export function extractCategoryFromRaw(raw: unknown): string | undefined {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }

  const rawObj = raw as Record<string, unknown>;

  // Try various possible locations for category data
  // 1. Direct category field
  if (typeof rawObj.category === 'string') {
    return rawObj.category;
  }

  // 2. Category object with name field
  if (rawObj.category && typeof rawObj.category === 'object') {
    const categoryObj = rawObj.category as Record<string, unknown>;
    if (typeof categoryObj.name === 'string') {
      return categoryObj.name;
    }
  }

  // 3. Feed object with category field
  if (rawObj.feed && typeof rawObj.feed === 'object') {
    const feedObj = rawObj.feed as Record<string, unknown>;
    if (typeof feedObj.category === 'string') {
      return feedObj.category;
    }
  }

  // 4. Metadata object with category field
  if (rawObj.metadata && typeof rawObj.metadata === 'object') {
    const metadataObj = rawObj.metadata as Record<string, unknown>;
    if (typeof metadataObj.category === 'string') {
      return metadataObj.category;
    }
  }

  return undefined;
}

/**
 * Convert array of ProductResult to array of RecommendedProduct
 */
export function convertAllToRecommendedProducts(products: ProductResult[]): RecommendedProduct[] {
  return products.map(convertToRecommendedProduct);
}

/**
 * Normalize recommendation products to UI format
 *
 * This function accepts products in either format and returns them
 * as RecommendedProduct[]. Used by UI components for compatibility.
 */
export function normalizeToRecommendedProducts(
  products: Array<ProductResult | RecommendedProduct>
): RecommendedProduct[] {
  return products.map(product => {
    if (isProductResult(product)) {
      return convertToRecommendedProduct(product);
    }
    return product;
  });
}